// ============================================================================
// AUDIO SYSTEM
// ============================================================================

// Import sound files - Vite will process these and provide URLs
// Using ?url suffix tells Vite to return the URL string instead of the asset
import slamNormalUrl from '../assets/slam-normal.mp3?url';
import slamHighUrl from '../assets/slam-high.mp3?url';
import slamHighestUrl from '../assets/slam-highest.mp3?url';
import woodCreakUrl from '../assets/wood-creak.mp3?url';
import woodSmash1Url from '../assets/wood-smash-1.mp3?url';
import woodSmash2Url from '../assets/wood-smash-2.mp3?url';
import woodSmash3Url from '../assets/wood-smash-3.mp3?url';

export class AudioSystem {
    constructor() {
        this.ctx = null;
        this.initialized = false;
        
        // Audio buffers for loaded sounds
        this.sounds = {
            slamNormal: null,
            slamHigh: null,
            slamHighest: null,
            woodCreak: null,
            woodSmash1: null,
            woodSmash2: null,
            woodSmash3: null
        };
        
        // Currently playing creak loop (for stopping)
        this.creakLoop = null;
        this.creakSource = null;
        
        // Track last collision time per throwable to prevent spam
        this.lastCollisionTime = new WeakMap();
        this.collisionCooldown = 0.1; // 100ms cooldown between collision sounds
    }

    async init() {
        if (this.initialized) return;
        try {
            this.ctx = new (window.AudioContext || window.webkitAudioContext)();
            
            // Resume audio context if suspended (browser security requires user interaction)
            if (this.ctx.state === 'suspended') {
                await this.ctx.resume();
            }
            
            this.initialized = true;
            console.log('Audio context initialized, state:', this.ctx.state);
            console.log('Audio context sample rate:', this.ctx.sampleRate);
            
            // Check if browser supports MP3 decoding
            // This is a simple test - try to decode a tiny MP3
            console.log('Testing MP3 decode support...');
            
            // Load all sound files
            await this.loadSounds();
            const loadedCount = Object.keys(this.sounds).filter(k => this.sounds[k] !== null).length;
            console.log(`Audio system ready. Loaded ${loadedCount}/${Object.keys(this.sounds).length} sounds`);
        } catch (e) {
            console.error('Web Audio API initialization failed:', e);
            this.initialized = false;
        }
    }
    
    async loadSounds() {
        // Map of sound keys to their imported URLs
        const soundUrls = {
            slamNormal: slamNormalUrl,
            slamHigh: slamHighUrl,
            slamHighest: slamHighestUrl,
            woodCreak: woodCreakUrl,
            woodSmash1: woodSmash1Url,
            woodSmash2: woodSmash2Url,
            woodSmash3: woodSmash3Url
        };
        
        for (const [key, url] of Object.entries(soundUrls)) {
            try {
                console.log(`Loading sound ${key} from: ${url}`);
                
                // Fetch the file using the Vite-processed URL
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                
                // Check content type
                const contentType = response.headers.get('content-type');
                console.log(`  Content-Type: ${contentType}`);
                
                const arrayBuffer = await response.arrayBuffer();
                console.log(`  ArrayBuffer size: ${arrayBuffer.byteLength} bytes`);
                
                // Try to decode the audio data
                // Note: decodeAudioData returns a Promise in newer browsers
                let audioBuffer;
                if (this.ctx.decodeAudioData.length === 1) {
                    // New API - returns Promise
                    audioBuffer = await this.ctx.decodeAudioData(arrayBuffer);
                } else {
                    // Old API - uses callbacks
                    audioBuffer = await new Promise((resolve, reject) => {
                        this.ctx.decodeAudioData(
                            arrayBuffer,
                            (buffer) => resolve(buffer),
                            (error) => reject(error)
                        );
                    });
                }
                
                this.sounds[key] = audioBuffer;
                console.log(`✓ Loaded sound: ${key} (${audioBuffer.duration.toFixed(2)}s, ${audioBuffer.sampleRate}Hz, ${audioBuffer.numberOfChannels} channels)`);
            } catch (e) {
                console.error(`✗ Failed to load sound ${key}:`, e);
                console.error(`  Error details:`, {
                    name: e.name,
                    message: e.message,
                    stack: e.stack
                });
            }
        }
    }
    
    /**
     * Get random pitch variation (not too much)
     * Returns a value between 0.9 and 1.1 (10% variation)
     */
    getRandomPitch() {
        return 0.9 + Math.random() * 0.2; // 0.9 to 1.1
    }
    
    /**
     * Get random volume variation
     * Returns a value between 0.85 and 1.0 (15% variation)
     */
    getRandomVolumeMultiplier() {
        return 0.85 + Math.random() * 0.15; // 0.85 to 1.0
    }
    
    /**
     * Get random pan position (-1 = left, 0 = center, 1 = right)
     * Returns a value between -0.3 and 0.3 (subtle stereo variation)
     */
    getRandomPan() {
        return (Math.random() - 0.5) * 0.6; // -0.3 to 0.3
    }
    
    /**
     * Play a sound with pitch variation
     * @param {AudioBuffer} buffer - The audio buffer to play
     * @param {number} volume - Volume (0-1)
     * @param {number} pitch - Pitch multiplier (1.0 = normal, 0.9-1.1 for variation)
     * @param {number} pan - Pan position (-1 = left, 0 = center, 1 = right)
     * @param {number} volumeVariation - Random volume multiplier (0.85-1.0)
     */
    async playSound(buffer, volume = 1.0, pitch = null, pan = null, volumeVariation = null) {
        if (!this.ctx || !buffer) {
            console.warn('Cannot play sound: ctx or buffer missing', { ctx: !!this.ctx, buffer: !!buffer });
            return null;
        }
        
        // Resume audio context if suspended
        if (this.ctx.state === 'suspended') {
            try {
                await this.ctx.resume();
                console.log('Audio context resumed');
            } catch (e) {
                console.warn('Failed to resume audio context:', e);
                return null;
            }
        }
        
        try {
            const source = this.ctx.createBufferSource();
            const gainNode = this.ctx.createGain();
            
            source.buffer = buffer;
            
            // Apply pitch variation if not specified
            if (pitch === null) {
                pitch = this.getRandomPitch();
            }
            source.playbackRate.value = pitch;
            
            // Apply volume variation if not specified
            if (volumeVariation === null) {
                volumeVariation = this.getRandomVolumeMultiplier();
            }
            
            // Set volume with variation
            gainNode.gain.value = volume * volumeVariation;
            
            // Apply panning (use random if not specified)
            if (pan === undefined) {
                pan = this.getRandomPan();
            }
            
            if (pan !== null && pan !== 0) {
                // Create stereo panner node
                const panner = this.ctx.createStereoPanner();
                panner.pan.value = pan;
                
                source.connect(gainNode);
                gainNode.connect(panner);
                panner.connect(this.ctx.destination);
            } else {
                // No panning (center)
                source.connect(gainNode);
                gainNode.connect(this.ctx.destination);
            }
            
            source.start(0);
            
            return source;
        } catch (e) {
            console.error('Error playing sound:', e);
            return null;
        }
    }
    
    /**
     * Play slam sound based on aggression level
     * @param {number} aggression - Current aggression value
     * @param {number} maxAggression - Maximum aggression for current level (CONFIG.maxConMaxAggression)
     */
    playSlam(aggression, maxAggression) {
        if (!this.ctx) {
            console.warn('Cannot play slam: audio context not initialized');
            return;
        }
        
        // Determine which slam sound to play
        let soundBuffer = null;
        
        // If no damage (aggression === 0), use normal slam
        if (aggression === 0) {
            soundBuffer = this.sounds.slamNormal;
        } else if (aggression < maxAggression * 0.5) {
            // Below 50% of max
            soundBuffer = this.sounds.slamHigh;
        } else {
            // Above 50% of max
            soundBuffer = this.sounds.slamHighest;
        }
        
        if (soundBuffer) {
            // Base volume (reduced by 30%)
            const baseVolume = 1.0 * 0.7;
            
            // Add slight volume variation based on aggression (more aggression = slightly louder)
            const aggressionVolumeBoost = Math.min(aggression / maxAggression, 1.0) * 0.1; // Up to 10% boost
            const volume = baseVolume * (1.0 + aggressionVolumeBoost);
            
            // Apply random variations: pitch, volume, and panning
            const pitch = this.getRandomPitch();
            const volumeVariation = this.getRandomVolumeMultiplier();
            const pan = this.getRandomPan();
            
            this.playSound(soundBuffer, volume, pitch, pan, volumeVariation);
        } else {
            console.warn('Slam sound buffer not loaded for aggression:', aggression);
        }
    }
    
    /**
     * Start playing wood creak on loop during hold
     */
    async startCreakLoop() {
        if (!this.ctx || !this.sounds.woodCreak) {
            console.warn('Cannot start creak loop:', { ctx: !!this.ctx, woodCreak: !!this.sounds.woodCreak });
            return;
        }
        
        // Resume audio context if suspended
        if (this.ctx.state === 'suspended') {
            try {
                await this.ctx.resume();
            } catch (e) {
                console.warn('Failed to resume audio context for creak:', e);
                return;
            }
        }
        
        // Stop existing loop if any
        this.stopCreakLoop();
        
        const playLoop = async () => {
            if (!this.creakLoop) return; // Stop if flag cleared
            
            try {
                const source = this.ctx.createBufferSource();
                const gainNode = this.ctx.createGain();
                
                source.buffer = this.sounds.woodCreak;
                source.playbackRate.value = this.getRandomPitch(); // Random pitch each loop
                gainNode.gain.value = 0.5 * 0.7; // Moderate volume, reduced by 30%
                
                source.connect(gainNode);
                gainNode.connect(this.ctx.destination);
                
                source.start(0);
                
                // When sound ends, wait 2-4 seconds before playing again
                source.onended = () => {
                    if (this.creakLoop) {
                        // Random delay between 2-4 seconds
                        const delay = 2000 + Math.random() * 2000; // 2000-4000ms
                        setTimeout(() => {
                            if (this.creakLoop) {
                                playLoop();
                            }
                        }, delay);
                    }
                };
                
                this.creakSource = source;
            } catch (e) {
                console.error('Error in creak loop:', e);
            }
        };
        
        this.creakLoop = true;
        playLoop();
    }
    
    /**
     * Stop the wood creak loop
     */
    stopCreakLoop() {
        this.creakLoop = false;
        if (this.creakSource) {
            try {
                this.creakSource.stop();
            } catch (e) {
                // Source may have already ended
            }
            this.creakSource = null;
        }
    }
    
    /**
     * Play wood smash sound when throwable hits something
     * @param {number} velocity - Impact velocity (for volume scaling)
     */
    async playWoodSmash(velocity = 0) {
        if (!this.ctx) {
            console.warn('Cannot play wood smash: audio context not initialized');
            return;
        }
        
        // Choose random wood-smash sound
        const smashSounds = [
            this.sounds.woodSmash1,
            this.sounds.woodSmash2,
            this.sounds.woodSmash3
        ].filter(s => s !== null); // Filter out any that failed to load
        
        if (smashSounds.length === 0) {
            console.warn('No wood smash sounds loaded');
            return;
        }
        
        const randomSound = smashSounds[Math.floor(Math.random() * smashSounds.length)];
        
        // Calculate volume based on velocity (slower = lower volume)
        // Normalize velocity to 0-1 range, then scale to 0.5-1.0 volume range (50% to 100%)
        // Assuming max velocity is around 1000 units/sec
        const maxVelocity = 1000;
        const normalizedVelocity = Math.min(Math.abs(velocity) / maxVelocity, 1.0);
        const volume = 0.5 + (normalizedVelocity * 0.5); // 0.5 (50%) to 1.0 (100%)
        
        this.playSound(randomSound, volume);
    }
    
    /**
     * Play wood smash with cooldown to prevent spam
     * @param {Object} throwableBody - The throwable body that hit something
     * @param {number} velocity - Impact velocity
     */
    playWoodSmashWithCooldown(throwableBody, velocity) {
        const now = performance.now() / 1000; // Current time in seconds
        const lastTime = this.lastCollisionTime.get(throwableBody) || 0;
        
        if (now - lastTime >= this.collisionCooldown) {
            this.lastCollisionTime.set(throwableBody, now);
            this.playWoodSmash(velocity);
        }
    }
    
    /**
     * Play alarm ring (synthesized, as no file provided)
     * TODO: Replace with actual sound file if provided
     */
    playAlarmRing() {
        if (!this.ctx) return;

        // Create a harsh, repetitive alarm sound
        const osc1 = this.ctx.createOscillator();
        const osc2 = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        // Two oscillators for a more complex, annoying sound
        osc1.type = 'square';
        osc1.frequency.value = 800;
        
        osc2.type = 'square';
        osc2.frequency.value = 1000;

        gain.gain.value = 0.3 * 0.7; // Reduced by 30%
        gain.gain.setValueAtTime(0.3 * 0.7, this.ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.5);

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(this.ctx.destination);

        osc1.start();
        osc2.start();
        osc1.stop(this.ctx.currentTime + 0.5);
        osc2.stop(this.ctx.currentTime + 0.5);
    }
}
